import { HTMLAttributes, useEffect, useState } from "react";

// Material UI component
import { Autocomplete, TextField, Box, CircularProgress, InputAdornment } from "@mui/material";

// Components
import { FieldHintButton } from "components/FieldHintButton";
import CustomIcon from "components/CustomIcon";
import icons from "enums/icons";

// Hooks
import { useAppSelector } from "app/hooks";
import { FormattedMessage, useIntl } from "react-intl";

// Services
import { fetchPeopleDiscoverService } from "services/people";

// Store
import { getBoardStructureData } from "store/slice/Team/boardStructure";

// utils
import { debounce } from "lodash";
import { limitFreemiumParams } from "utils";
import { getPeopleParams } from "services/people";

// Styles
import styles from "./styles";

// Type
import { SearchPerson, TOnSelectNewMember } from "../Team.types";
import { Member } from "store/slice/Team/team.types";
import { PersonSearch } from "services/people/people.types";
import { RequestInfo } from "store/slice/store.types";

// Abilities
import { useAbility } from "@casl/react";
import { AbilityContext } from "context/Ability";
import CantFindData from "components/Pro/CantFindData/CantFindData";

type AutocompleteSearchPersonProps = {
    onSelectNewMember?: TOnSelectNewMember;
    addToList?: Member[];
};

const initialInternalValue: SearchPerson = {
    name: "",
    person_id: 0,
    linkedin_id: "",
    contacts: [],
    current: {
        company_name: "",
        company_id: 0,
        role: "",
        roleKey: "",
        email: "",
    },
};

const initialValue: { data: SearchPerson[]; status: RequestInfo } = { data: [], status: "pristine" };
const columns = [
    "id",
    "name",
    "linkedin_id",
    "work_history.is_current_employment",
    "work_history.company_id",
    "work_history.company_name",
    "work_history.work_position_name",
    "work_history.unlinked_company",
    "contacts.id",
    "contacts.value",
    "contacts.type",
    "contacts.nature",
    "contacts.is_primary",
    "contacts.is_hidden",
    "user_is_registered",
    "user_is_optin_share_data",
    "user_is_pace",
];

const AutocompleteSearchPerson = ({ onSelectNewMember, addToList }: AutocompleteSearchPersonProps): JSX.Element => {
    const [options, setOptions] = useState<{ data: SearchPerson[]; status: RequestInfo }>(initialValue);
    const [internalValue, setInternalValue] = useState(initialInternalValue);
    const selectedBoardMembersList = useAppSelector(getBoardStructureData);
    const intl = useIntl();

    const ability = useAbility(AbilityContext);
    const isFreemium = ability.can("see", "LD_FREE");

    const classes = styles.autocompleteSearchPerson();

    const searchElements = debounce(async (value: string) => {
        try {
            setOptions((currentValue) => ({ ...currentValue, status: "fetching" }));
            const params = getPeopleParams({
                termFilter: value,
                termField: "name",
                sort: { "name.keyword": { order: "asc" } },
                meta: { page_no: 1, page_size: 50 },
                columns: limitFreemiumParams(columns, isFreemium),
            });

            const { data } = await fetchPeopleDiscoverService<PersonSearch>(params);

            const searchPeople = data.results.map((personResult) => {
                const emailContact =
                    personResult.contacts?.filter(({ nature, type }) => nature === "business" && type === "email") ??
                    [];

                const currentWorkHistory = personResult.work_history?.filter(
                    ({ is_current_employment }) => is_current_employment
                );

                return {
                    name: personResult.name ?? "",
                    person_id: personResult.id,
                    ...(personResult?.linkedin_id ? { linkedin_id: personResult.linkedin_id } : {}),
                    ...(personResult?.contacts ? { contacts: personResult.contacts } : {}),
                    user_is_registered: personResult.user_is_registered,
                    user_is_optin_share_data: personResult.user_is_optin_share_data,
                    user_is_pace: personResult.user_is_pace,
                    current: {
                        company_name: currentWorkHistory.length
                            ? currentWorkHistory[0].company_name
                                ? currentWorkHistory[0].company_name
                                : currentWorkHistory[0].unlinked_company ?? ""
                            : "",
                        company_id: currentWorkHistory.length ? currentWorkHistory[0].company_id : null,
                        role: currentWorkHistory.length ? currentWorkHistory[0].work_position_name : "",
                        roleKey: "",
                        ...(emailContact?.length ? { email: emailContact[0].value } : { email: "" }),
                    },
                } as SearchPerson;
            });

            setOptions({ data: searchPeople, status: "done" });
        } catch (error) {
            setOptions({ data: [], status: "done" });
            console.error(error);
        }
    }, 500);

    const handleInputChange = (event: React.ChangeEvent<unknown>, value: string, reason: string) => {
        if (reason === "clear" || reason === "reset") {
            setOptions(initialValue);
            return;
        }
        if (value.length < 2) {
            setOptions(initialValue);
            return;
        }
        searchElements(value);
    };

    const handleSelectChange = (ev: React.SyntheticEvent, value: SearchPerson | null) => {
        if (!value) return;

        setInternalValue(value);

        if (value) {
            const isMemberAlreadySelected = () =>
                selectedBoardMembersList.some((member) => member.person_id === value.person_id);

            if (!isMemberAlreadySelected()) {
                onSelectNewMember && addToList && onSelectNewMember(ev, value, addToList);
                setInternalValue(initialInternalValue);
                setOptions(initialValue);
            }
        }
    };

    const getOptionLabel = (option: SearchPerson) => option.name;
    const getOptionSelected = (option: SearchPerson, value: SearchPerson) => {
        return option.name === value.name;
    };

    const OptionRenderer = (props: HTMLAttributes<HTMLLIElement>, option: SearchPerson) => {
        const companyName = option.current.company_name;

        return (
            <li {...props} key={props.id}>
                <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="flex-start"
                    className={classes.optionContainer}
                >
                    <span className={classes.optionNameLabel}>{option.name}</span>
                    <Box display="flex" flexDirection="row" justifyContent="flex-start" alignItems="center">
                        <Box order={1} pr={1}>
                            <p className={classes.optionCurrentWorkInfo}>
                                {option.current.role || <strong>?</strong>}
                                {companyName && <span> @ {companyName}</span>}
                            </p>
                        </Box>
                    </Box>
                </Box>
            </li>
        );
    };

    useEffect(() => searchElements.cancel(), [searchElements]);

    return (
        <Autocomplete
            disableClearable
            forcePopupIcon={false}
            id="search-person-select"
            options={options.data}
            value={internalValue}
            loading={options.status === "fetching"}
            isOptionEqualToValue={getOptionSelected}
            getOptionLabel={getOptionLabel}
            renderOption={OptionRenderer}
            onInputChange={handleInputChange}
            onChange={handleSelectChange}
            noOptionsText={options.status === "done" ? <CantFindData /> : "No options"}
            filterOptions={(options: SearchPerson[], state: object) => options}
            classes={{
                root: classes.autoCompleteSearchRoot,
            }}
            popupIcon={
                options.status === "fetching" ? (
                    <CircularProgress color="primary" size={20} />
                ) : (
                    <CustomIcon icon={icons.search} />
                )
            }
            renderInput={(params) => (
                <TextField
                    {...params}
                    size="small"
                    placeholder={intl.formatMessage({
                        id: "setup-company.team.searchPersonField.placeholder",
                    })}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <InputAdornment position="end">
                                {options.status === "fetching" ? (
                                    <CircularProgress color="primary" size={20} />
                                ) : (
                                    <CustomIcon icon={icons.search} />
                                )}
                                {/* Hint button */}
                                <FieldHintButton>
                                    <FormattedMessage id="field.hint.people.search" />
                                </FieldHintButton>
                            </InputAdornment>
                        ),
                    }}
                />
            )}
        />
    );
};

export default AutocompleteSearchPerson;
